import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/context";
import { fetchUnreadCount } from "../notifications/api";

// Bell in the header.  Polls /unread-count every minute while authenticated;
// the page itself owns the full list.  We deliberately avoid a global
// notifications store — the inbox is low-volume and read-mostly.

const POLL_MS = 60 * 1000;

export function NotificationBell() {
  const { state } = useAuth();
  const accessToken =
    (state.status === "authenticated" && state.session.access_token) ||
    (state.status === "offline" && state.session.access_token) ||
    null;

  const [count, setCount] = useState<number>(0);

  useEffect(() => {
    if (!accessToken) return;
    let alive = true;
    const tick = async () => {
      try {
        const n = await fetchUnreadCount(accessToken);
        if (alive) setCount(n);
      } catch {
        // Network down / unauth — leave the last count, don't churn UI.
      }
    };
    void tick();
    const id = window.setInterval(() => void tick(), POLL_MS);
    // Re-poll on tab focus so a user returning to the app sees a fresh count
    // immediately instead of waiting up to a minute.
    const onFocus = () => {
      if (document.visibilityState === "visible") void tick();
    };
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      alive = false;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [accessToken]);

  if (!accessToken) return null;

  return (
    <Link
      to="/notifications"
      className="relative inline-flex items-center rounded-md px-1.5 py-1 text-base hover:bg-slate-100 dark:hover:bg-slate-800"
      title={count > 0 ? `${count} unread notification${count === 1 ? "" : "s"}` : "Notifications"}
      aria-label="Notifications"
    >
      <span aria-hidden>🔔</span>
      {count > 0 && (
        <span className="absolute -right-0.5 -top-0.5 min-w-[1.1rem] rounded-full bg-rose-500 px-1 text-center text-[10px] font-semibold leading-[1.1rem] text-white">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}
